import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { TrendingUp, TrendingDown, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function AnalysisPage() {
  const { isAuthenticated } = useAuth();
  const [tickerData, setTickerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchTickerData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/market/ticker');
      setTickerData(response.data.data);
      setLastUpdated(new Date());
    } catch (error) {
      // Ticker data fetch failed
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickerData();
    const interval = setInterval(fetchTickerData, 30000);
    return () => clearInterval(interval);
  }, [fetchTickerData]);

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <nav className="glass-header border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="analysis-logo">
            <div className="w-10 h-10 bg-[#007AFF] rounded-sm flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              COSSY NETWORK
            </span>
          </Link>
          
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button className="btn-primary" data-testid="dashboard-btn">Dashboard</Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="btn-primary" data-testid="login-btn">Sign In</Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="analysis-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-[#71717A] hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Market Analysis
            </h1>
            <p className="text-[#71717A] mt-2">Live trading data for major market indices</p>
          </div>
          
          <div className="text-right">
            <Button 
              variant="outline" 
              className="border-[#27272A] text-[#A1A1AA] hover:text-white"
              onClick={fetchTickerData}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {lastUpdated && (
              <p className="text-[#71717A] text-sm mt-2">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>

        {/* Ticker Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {tickerData.map(item => (
            <div 
              key={item.symbol}
              className="bg-[#141417] border border-[#27272A] rounded-md p-6 hover:border-[#007AFF]/50 transition-colors"
              data-testid={`ticker-${item.symbol}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-lg font-bold text-white">{item.symbol}</span>
                <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                  item.change >= 0 ? 'bg-[#00FF66]/10' : 'bg-[#FF3333]/10'
                }`}>
                  {item.change >= 0 ? (
                    <TrendingUp className="w-5 h-5 text-[#00FF66]" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-[#FF3333]" />
                  )}
                </div>
              </div>
              <p className="text-[#71717A] text-sm mb-2">{item.name}</p>
              <div className="text-3xl font-bold text-white font-mono mb-2">
                ${item.price.toFixed(2)}
              </div>
              <div className={`font-mono text-sm ${item.change >= 0 ? 'ticker-up' : 'ticker-down'}`}>
                {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)} ({item.change_percent.toFixed(2)}%)
              </div>
            </div>
          ))}
        </div>

        {/* Chart Placeholder */}
        <div className="bg-[#141417] border border-[#27272A] rounded-md p-8">
          <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Market Overview
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* S&P 500 Chart Placeholder */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-medium">S&P 500 (SPY)</h3>
                <span className="ticker-up font-mono text-sm">+0.45%</span>
              </div>
              <div className="h-48 bg-[#09090B] border border-[#27272A] rounded-md flex items-end p-4 gap-1">
                {[40, 45, 42, 48, 55, 52, 58, 60, 55, 62, 65, 60, 68, 70, 72, 68, 75, 78, 76, 80].map((h, i) => (
                  <div 
                    key={`spy-${i}`} 
                    className="flex-1 bg-gradient-to-t from-[#007AFF] to-[#007AFF]/30 rounded-t-sm" 
                    style={{ height: `${h}%` }}
                  ></div>
                ))}
              </div>
            </div>

            {/* Gold Chart Placeholder */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-medium">Gold (GLD)</h3>
                <span className="ticker-up font-mono text-sm">+0.28%</span>
              </div>
              <div className="h-48 bg-[#09090B] border border-[#27272A] rounded-md flex items-end p-4 gap-1">
                {[60, 62, 58, 65, 68, 64, 70, 72, 68, 75, 78, 74, 80, 76, 82, 78, 85, 80, 88, 85].map((h, i) => (
                  <div 
                    key={`gld-${i}`} 
                    className="flex-1 bg-gradient-to-t from-yellow-500 to-yellow-500/30 rounded-t-sm" 
                    style={{ height: `${h}%` }}
                  ></div>
                ))}
              </div>
            </div>
          </div>

          {/* Market Summary */}
          <div className="mt-8 pt-8 border-t border-[#27272A]">
            <h3 className="text-white font-medium mb-4">Market Summary</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-[#09090B] border border-[#27272A] rounded-md p-4">
                <p className="text-[#71717A] text-sm mb-1">Market Status</p>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#00FF66] rounded-full animate-pulse"></span>
                  <span className="text-white font-medium">Markets Open</span>
                </div>
              </div>
              <div className="bg-[#09090B] border border-[#27272A] rounded-md p-4">
                <p className="text-[#71717A] text-sm mb-1">Trading Session</p>
                <span className="text-white font-medium">Regular Hours</span>
              </div>
              <div className="bg-[#09090B] border border-[#27272A] rounded-md p-4">
                <p className="text-[#71717A] text-sm mb-1">Data Source</p>
                <span className="text-white font-medium">Real-time Feed</span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        {!isAuthenticated && (
          <div className="mt-12 bg-gradient-to-r from-[#007AFF]/10 to-transparent border border-[#007AFF]/30 rounded-md p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Get Access to Premium Analysis
            </h2>
            <p className="text-[#A1A1AA] mb-6">
              Join our trading academy for in-depth market analysis and trading signals
            </p>
            <Link to="/register">
              <Button className="btn-primary" data-testid="join-now-btn">Join Now</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
